<div class="scope-tree" role="tree" aria-label="Scope Hierarchy Tree">
  @for (group of groups; track group.region) {
    <div class="scope-tree__group"
         [class.scope-tree__group--collapsed]="isRegionCollapsed(group.region)"
         [class.scope-tree__group--disabled]="group.companyCount === 0"
         role="treeitem"
         [attr.aria-expanded]="!isRegionCollapsed(group.region)"
         [attr.aria-selected]="isRegionSelected(group) ? 'true' : 'false'"
         [attr.aria-disabled]="group.companyCount === 0">

      <!-- Region Header -->
      <div class="scope-tree__head">
        <div class="scope-tree__head-left cursor-pointer"
             (click)="onRegionHeadClick(group, $event)"
             [title]="isRegionCollapsed(group.region) ? 'Click to expand ' + group.label : 'Click to collapse ' + group.label">
          <button type="button"
                  class="scope-tree__expander"
                  [attr.aria-label]="(isRegionCollapsed(group.region) ? 'Expand ' : 'Collapse ') + group.label"
                  (click)="toggleRegionCollapse(group.region, $event)">
            <i class="pi" [class.pi-chevron-down]="!isRegionCollapsed(group.region)" [class.pi-chevron-right]="isRegionCollapsed(group.region)"></i>
          </button>
          <i class="pi pi-globe scope-tree__region-icon"></i>
          <span class="scope-tree__title" [class.font-bold]="isRegionSelected(group)">{{ group.label }}</span>

          <!-- Aggregate Status badge in Country or Company mode -->
          @if (selectionLevel !== 'region') {
            @if (isRegionSelected(group)) {
              <span class="scope-tree__status-badge scope-tree__status-badge--full" title="All constituent countries and companies selected">
                <i class="pi pi-check text-xs"></i> (All Selected)
              </span>
            }
          }
        </div>

        <div class="scope-tree__head-right flex align-items-center gap-2">
          <span class="scope-tree__meta">
            {{ group.countryCount }} {{ group.countryCount === 1 ? 'country' : 'countries' }}, {{ group.companyCount }} {{ group.companyCount === 1 ? 'company' : 'companies' }}
          </span>

          @if (group.companyCount === 0) {
            <span class="scope-tree__unconfigured-badge" [title]="getEmptyRegionMessage(group)">
              <i class="pi pi-exclamation-triangle"></i> No companies
            </span>
          }

          <!-- Select control only appears in Region mode -->
          @if (selectionLevel === 'region') {
            <button type="button"
                    class="p-button p-button-xs p-button-text scope-tree__select-btn"
                    [class.scope-tree__select-btn--active]="isRegionSelected(group)"
                    [disabled]="group.companyCount === 0"
                    [title]="group.companyCount === 0 ? getEmptyRegionMessage(group) : (isRegionSelected(group) ? 'Deselect Region ' + group.label : 'Select Region ' + group.label)"
                    [attr.aria-label]="'Select region ' + group.label"
                    [attr.aria-checked]="isRegionSelected(group) ? 'true' : (isRegionIndeterminate(group) ? 'mixed' : 'false')"
                    (click)="onRegionSelectClick(group, $event)">
              <i class="pi text-xs mr-1"
                 [class.pi-check-circle]="isRegionSelected(group)"
                 [class.pi-minus-circle]="isRegionIndeterminate(group)"
                 [class.pi-circle]="!isRegionSelected(group) && !isRegionIndeterminate(group)"></i>
              {{ isRegionSelected(group) ? 'Deselect Region' : 'Select Region' }}
            </button>
          }
        </div>
      </div>

      <!-- Countries List -->
      @if (!isRegionCollapsed(group.region)) {
        <div class="scope-tree__children" role="group">
          @for (c of group.countries; track c.country) {
            <div class="scope-tree__country-node"
                 [class.scope-tree__country-node--collapsed]="isCountryCollapsed(group.region, c.country)"
                 [class.scope-tree__country-node--unconfigured]="c.companies.length === 0"
                 role="treeitem"
                 [attr.aria-expanded]="!isCountryCollapsed(group.region, c.country)"
                 [attr.aria-selected]="isCountrySelected(c, group) ? 'true' : 'false'"
                 [attr.aria-disabled]="c.companies.length === 0">

              <!-- Country Sub-header -->
              <div class="scope-tree__sub">
                <div class="scope-tree__sub-left cursor-pointer"
                     (click)="onCountryHeadClick(c, group, $event)"
                     [title]="isCountryCollapsed(group.region, c.country) ? 'Click to expand ' + c.country : 'Click to collapse ' + c.country">
                  <button type="button"
                          class="scope-tree__expander"
                          [attr.aria-label]="(isCountryCollapsed(group.region, c.country) ? 'Expand ' : 'Collapse ') + c.country"
                          (click)="toggleCountryCollapse(group.region, c.country, $event)">
                    <i class="pi" [class.pi-chevron-down]="!isCountryCollapsed(group.region, c.country)" [class.pi-chevron-right]="isCountryCollapsed(group.region, c.country)"></i>
                  </button>
                  <i class="pi pi-flag scope-tree__country-icon"
                     [class.text-primary]="isCountrySelected(c, group)"></i>
                  <span class="scope-tree__subtitle"
                        [class.font-bold]="isCountrySelected(c, group)"
                        [class.text-primary]="isCountrySelected(c, group)">
                    {{ c.country }}
                  </span>

                  <!-- Visual inclusion in region mode -->
                  @if (selectionLevel === 'region' && isRegionSelected(group)) {
                    <span class="scope-tree__included-badge" title="Implicitly included by selected region">
                      <i class="pi pi-check text-xs"></i> (Included)
                    </span>
                  }

                  <!-- Aggregate Status badge in company mode -->
                  @if (selectionLevel === 'company') {
                    @if (isCountrySelected(c, group)) {
                      <span class="scope-tree__status-badge scope-tree__status-badge--full" title="All companies in this country selected">
                        <i class="pi pi-check text-xs"></i> (All Selected)
                      </span>
                    }
                  }
                </div>

                <div class="scope-tree__sub-right flex align-items-center gap-2">
                  <span class="scope-tree__meta">{{ c.companies.length }} {{ c.companies.length === 1 ? 'company' : 'companies' }}</span>

                  @if (c.companies.length === 0) {
                    <span class="settings__chip-unconfigured-badge" [title]="'No companies mapped to ' + c.country">
                      Unconfigured
                    </span>
                  }

                  <!-- Select control only appears in Country mode -->
                  @if (selectionLevel === 'country') {
                    <button type="button"
                            class="p-button p-button-xs p-button-text scope-tree__select-btn"
                            [class.scope-tree__select-btn--active]="isCountrySelected(c, group)"
                            [disabled]="c.companies.length === 0"
                            [title]="c.companies.length === 0 ? 'No companies mapped to ' + c.country : (isCountrySelected(c, group) ? 'Deselect Country ' + c.country : 'Select Country ' + c.country)"
                            [attr.aria-label]="'Select country ' + c.country"
                            [attr.aria-checked]="isCountrySelected(c, group) ? 'true' : (isCountryIndeterminate(c, group) ? 'mixed' : 'false')"
                            (click)="onCountrySelectClick(c, group, $event)">
                      <i class="pi text-xs mr-1"
                         [class.pi-check-circle]="isCountrySelected(c, group)"
                         [class.pi-minus-circle]="isCountryIndeterminate(c, group)"
                         [class.pi-circle]="!isCountrySelected(c, group) && !isCountryIndeterminate(c, group)"></i>
                      {{ isCountrySelected(c, group) ? 'Deselect' : 'Select All' }}
                    </button>
                  }
                </div>
              </div>

              <!-- Company Chips -->
              @if (!isCountryCollapsed(group.region, c.country)) {
                <div class="settings__scope-chips-grid scope-tree__chips-grid" role="group">
                  @for (comp of c.companies; track comp) {
                    @if (selectionLevel === 'company') {
                      <button type="button"
                              class="settings__kpi-card__option settings__kpi-card__option--chip scope-tree__chip"
                              [class.scope-tree__chip--active]="isCompanyActiveInTree(comp, c, group)"
                              [class.settings__kpi-card__option--active]="isCompanyActiveInTree(comp, c, group)"
                              role="treeitem"
                              [attr.aria-label]="'Select company ' + formatCompanyDisplay(comp)"
                              [attr.aria-checked]="isCompanyActiveInTree(comp, c, group) ? 'true' : 'false'"
                              (click)="onCompanyClick(comp)">
                        @if (isCompanyActiveInTree(comp, c, group)) {
                          <i class="pi pi-check text-xs"></i>
                        }
                        <span>{{ formatCompanyDisplay(comp) }}</span>
                      </button>
                    } @else {
                      <span class="settings__kpi-card__option settings__kpi-card__option--chip scope-tree__chip scope-tree__chip--readonly"
                            [class.scope-tree__chip--active]="isCompanyActiveInTree(comp, c, group)"
                            [class.scope-tree__chip--inherited]="isCompanyInherited(c, group)"
                            role="treeitem"
                            [attr.aria-label]="formatCompanyDisplay(comp) + (isCompanyActiveInTree(comp, c, group) ? ' (Active)' : '')">
                        @if (isCompanyActiveInTree(comp, c, group)) {
                          <i class="pi pi-check text-xs"></i>
                        }
                        <span>{{ formatCompanyDisplay(comp) }}</span>
                        @if (isCompanyInherited(c, group)) {
                          <span class="scope-tree__chip-inherited-tag">Included</span>
                        }
                      </span>
                    }
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  }
</div>
